// Type-only import: the runtime side of the card pulls in icon modules, which unit tests
// running this pure predicate have no reason to load.
import type { TransferProgressData } from "./transfer-progress-card";

/**
 * Whether the card still offers the move into cross margin.
 *
 * The single answer to that question: `transfer-progress-container.tsx` renders the action from
 * it, and `isTransferComplete` reads its absence as "nothing left to do".
 *
 * A missing `sweepable` — a card persisted before the field existed — counts as false, because
 * the sweep aborts on chain for an asset the exchange does not take as collateral.
 *
 * Internal to the capability model; UI consumers call `deriveCapabilities`.
 */
export function hasSweepAction(transfer: TransferProgressData): boolean {
  if (transfer.type === "fun-deposit") {
    // Only the delivery's owner offers the move; the other items it covers end at "available".
    return (
      transfer.currentStep === "available" &&
      transfer.projection.nextAction === "confirm_deposit" &&
      !!transfer.sweepable
    );
  }
  return (
    transfer.type === "xchain-deposit" &&
    transfer.currentStep === "available" &&
    !!transfer.sweepable
  );
}

/**
 * Whether the card has nothing left to do.
 *
 * This gates the "complete" wording in the title. Closing is a weaker condition — see
 * `isTransferDismissible`.
 *
 * Internal to the capability model; UI consumers read `deriveCapabilities(...).isTerminal`.
 */
export function isTransferComplete(transfer: TransferProgressData): boolean {
  if (transfer.currentStep === "completed") return true;
  if (transfer.currentStep === "transfer-failed") return true;

  const isCancelled = transfer.currentStep === "withdrawal-cancelled";
  if (isCancelled) return true;

  // Queued withdrawals end at "available" — there is no "completed" step for them to reach.
  if (transfer.type === "queued-withdraw" || transfer.type === "queued-xchain-withdraw") {
    return transfer.currentStep === "available";
  }

  // A delivered cross-chain deposit normally waits at "available" for the user to move the funds
  // into collateral, and reaches "completed" when they do. An asset that cannot be moved has no
  // such step to reach, so it is done on arrival — which is what makes it read as complete rather
  // than in progress, and what puts the dismiss button on it.
  if (transfer.type === "xchain-deposit" || transfer.type === "fun-deposit") {
    if (hasSweepAction(transfer)) return false;
    return transfer.currentStep === "available";
  }

  return false;
}

/**
 * A card the previous release persisted at `confirming`, back when an unclassifiable delivery
 * still mapped to a step. `xchainStepForStage` no longer produces one, so nothing will move these
 * on and they are permanent without an escape. Delete once no client can still be carrying one.
 *
 * Also what makes closing one reversible: `forgetDismissedDelivery` releases the delivery from the
 * imported-deposits ledger for exactly these, so a delivery that is classified later gets a fresh
 * card with its sweep rather than being suppressed forever.
 *
 * Internal to the capability model, but also read directly by `xchain-imported-deposits.ts:44`.
 */
export function isStrandedXchainDeposit(
  transfer: TransferProgressData,
): transfer is TransferProgressData & { type: "xchain-deposit" } {
  return transfer.type === "xchain-deposit" && transfer.currentStep === "confirming";
}

/**
 * Whether the user is allowed to close the card, which is weaker than it being finished — the
 * stranded cards above are closeable without ever completing.
 *
 * Stated as "finished, or known to be stranded" rather than "not currently offering an action",
 * so a step nobody has enumerated yet stays closed off by default. Closing cannot be undone: a
 * delivered deposit still offering its sweep is the only surface that moves funds out of the
 * primary subaccount's fungible store, and the imported-deposits ledger stops a closed card
 * coming back. A CCTP card at `claimable` holds the attestation as well as the claim.
 *
 * Internal to the capability model; UI consumers read `deriveCapabilities(...).isDismissible`.
 */
export function isTransferDismissible(transfer: TransferProgressData): boolean {
  if (transfer.type === "fun-deposit" && transfer.projection.dismissible) return true;
  return isTransferComplete(transfer) || isStrandedXchainDeposit(transfer);
}
